import { HttpErrorResponse } from '@angular/common/http';
import { getAuthUserFromCookie } from 'src/@core/utils/auth-storage';
import { ChatMessage, ChatMessageInteractiveOptions } from '../models/chat-message.model';
import { ChatPresetQuestion } from '../models/chat-preset-question.model';
import { ChatUiError } from '../models/chat-ui-error.model';
import {
  ChatConversationDetailDto,
  ChatConversationMessageDto,
  ChatConversationSummaryDto,
  ChatRequestContextPayload,
  ChatResponse,
  ChatUsageBalance,
} from '../models/chat-session-api.model';

export function extractAssistantContent(response: ChatResponse | string | null | undefined) {
  if (!response) {
    return null;
  }
  if (typeof response === 'string') {
    return response;
  }

  if (Array.isArray(response)) {
    const parts = response
      .map((item) => {
        if (typeof item === 'string') {
          return item;
        }
        if (item?.output && typeof item.output === 'string') {
          return item.output;
        }
        if (item?.message && typeof item.message === 'string') {
          return item.message;
        }
        return null;
      })
      .filter((part): part is string => !!part);
    if (parts.length) {
      return parts.join('\n\n');
    }
  }

  const data = (response as any).data;
  if (data) {
    if (data.assistantMessage && typeof data.assistantMessage.content === 'string') {
      return data.assistantMessage.content;
    }
    if (typeof data.answer === 'string') {
      return data.answer;
    }
    if (typeof data === 'string') {
      return data;
    }
    if (typeof data.reply === 'string') {
      return data.reply;
    }
    if (typeof data.text === 'string') {
      return data.text;
    }
    if (typeof data.message === 'string') {
      return data.message;
    }
    if (data.message && typeof data.message.content === 'string') {
      return data.message.content;
    }
  }

  const message = (response as ChatResponse).message;
  if (typeof message === 'string' && !isStatusMessage(message)) {
    return message;
  }
  if (message && typeof (message as ChatMessage).content === 'string') {
    return (message as ChatMessage).content;
  }

  if (typeof (response as any).reply === 'string') {
    return (response as any).reply;
  }
  if (typeof (response as any).response === 'string') {
    return (response as any).response;
  }
  if (typeof (response as any).text === 'string') {
    return (response as any).text;
  }
  if (typeof (response as any).result === 'string') {
    return (response as any).result;
  }

  return null;
}

export function extractApplicationError(response: ChatResponse | string | null | undefined): ChatUiError | null {
  if (!response || typeof response === 'string' || Array.isArray(response)) {
    return null;
  }

  if ((response as any).isPassed === false) {
    return toChatUiError(response as ChatResponse);
  }

  return null;
}

export function extractUsageBalance(response: unknown): ChatUsageBalance | null {
  if (!response || typeof response !== 'object' || Array.isArray(response)) {
    return null;
  }

  const data = (response as any).data;
  const candidate = data?.usageBalance || data?.balance || data || response;
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  const remainingTokens = Number((candidate as any).remainingTokens);
  if (!Number.isFinite(remainingTokens)) {
    return null;
  }

  return {
    remainingTokens: Math.max(0, remainingTokens),
    remainingBirr: Number((candidate as any).remainingBirr) || 0,
    remainingUsd: Number((candidate as any).remainingUsd) || 0,
    updatedAt: `${(candidate as any).updatedAt || ''}`,
  };
}

export function extractConversationId(response: ChatResponse | string | null | undefined): string | null {
  if (!response || typeof response === 'string') {
    return null;
  }

  const fromData = `${(response as any).data?.conversationId || ''}`.trim();
  if (fromData) {
    return fromData;
  }

  const fromRoot = `${(response as any).conversationId || ''}`.trim();
  return fromRoot || null;
}

export function extractConversationTitle(response: ChatResponse | string | null | undefined): string | null {
  if (!response || typeof response === 'string') {
    return null;
  }

  const fromData = `${(response as any).data?.title || ''}`.trim();
  if (fromData) {
    return fromData;
  }

  return null;
}

export function extractAssistantMessage(response: ChatResponse | string | null | undefined): ChatConversationMessageDto | null {
  if (!response || typeof response === 'string') {
    return null;
  }

  const data = (response as any).data;
  const candidate = data?.assistantMessage || (response as any).assistantMessage;
  if (!candidate || typeof candidate !== 'object') {
    return null;
  }

  return {
    id: `${candidate.id || ''}`.trim() || undefined,
    role: (candidate.role || 'assistant') as ChatMessage['role'],
    content: `${candidate.content || ''}`,
    timestamp: candidate.timestamp || new Date().toISOString(),
    responseRating: (candidate.responseRating as 'good' | 'bad' | null | undefined) || null,
    interactive: extractInteractiveOptions(response),
  };
}

export function extractInteractiveOptions(response: ChatResponse | string | null | undefined): ChatMessageInteractiveOptions | null {
  if (!response || typeof response === 'string') {
    return null;
  }

  const interactive = (response as any).data?.interactive || (response as any).interactive;
  if (!interactive || typeof interactive !== 'object' || Array.isArray(interactive)) {
    return null;
  }

  const mapped: ChatMessageInteractiveOptions = {
    products: toInteractiveOptionList(interactive.products),
    facilities: toInteractiveOptionList(interactive.facilities),
    woredas: toInteractiveOptionList(interactive.woredas),
    zones: toInteractiveOptionList(interactive.zones),
    hubs: toInteractiveOptionList(interactive.hubs),
    regions: toInteractiveOptionList(interactive.regions),
  };

  return hasInteractiveOptions(mapped) ? mapped : null;
}

export function toInteractiveOptionList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => `${item || ''}`.trim())
    .filter((item) => !!item);
}

export function hasInteractiveOptions(value: ChatMessageInteractiveOptions | null | undefined): boolean {
  if (!value) {
    return false;
  }

  return !!(
    value.products?.length ||
    value.facilities?.length ||
    value.woredas?.length ||
    value.zones?.length ||
    value.hubs?.length ||
    value.regions?.length
  );
}

export function toConversationSummaryList(response: unknown): ChatConversationSummaryDto[] {
  if (Array.isArray(response)) {
    return response as ChatConversationSummaryDto[];
  }

  if (response && typeof response === 'object' && Array.isArray((response as any).data)) {
    return (response as any).data as ChatConversationSummaryDto[];
  }

  return [];
}

export function toConversationDetail(response: unknown): ChatConversationDetailDto | null {
  if (!response) {
    return null;
  }

  if (response && typeof response === 'object' && (response as any).data) {
    return (response as any).data as ChatConversationDetailDto;
  }

  if (response && typeof response === 'object' && typeof (response as any).id === 'string') {
    return response as ChatConversationDetailDto;
  }

  return null;
}

export function toPresetQuestionList(response: unknown): ChatPresetQuestion[] {
  const items = Array.isArray(response)
    ? response
    : response && typeof response === 'object' && Array.isArray((response as any).data)
      ? (response as any).data
      : [];

  return items
    .map((item: any) => ({
      id: `${item?.id || ''}`.trim(),
      label: `${item?.label || item?.question || item?.name || ''}`.trim(),
    }))
    .filter((item: ChatPresetQuestion) => !!item.id && !!item.label);
}

export function buildRequestContext(): ChatRequestContextPayload | undefined {
  const params = readRouteQueryParams();
  const user = readLoggedUser();
  const forecastYear = parsePositiveInt(params.get('forecastYear') || params.get('year'));
  const laboratoryId = parsePositiveInt(params.get('laboratoryId') || params.get('labId'))
    ?? parsePositiveIntFromUnknown(user?.laboratoryId ?? user?.labId ?? user?.laboratory?.id);
  const woredaId = parsePositiveInt(params.get('woredaId'))
    ?? parsePositiveIntFromUnknown(user?.woredaId ?? user?.woreda?.id);
  const zoneId = parsePositiveInt(params.get('zoneId'))
    ?? parsePositiveIntFromUnknown(user?.zoneId ?? user?.zone?.id);
  const regionId = parsePositiveInt(params.get('regionId'))
    ?? parsePositiveIntFromUnknown(user?.regionId ?? user?.region?.id);
  const hubId = parsePositiveInt(params.get('hubId'))
    ?? parsePositiveIntFromUnknown(user?.hubId ?? user?.hub?.id);
  const scopeTier = (params.get('scopeTier') || params.get('scopeLevel') || '').trim()
    || resolveUserScopeTier(user);

  if (!forecastYear && !laboratoryId && !woredaId && !zoneId && !regionId && !hubId && !scopeTier) {
    return undefined;
  }

  const context: ChatRequestContextPayload = {};
  if (forecastYear) {
    context.forecastYear = forecastYear;
  }
  if (laboratoryId) {
    context.laboratoryId = laboratoryId;
  }
  if (woredaId) {
    context.woredaId = woredaId;
  }
  if (zoneId) {
    context.zoneId = zoneId;
  }
  if (regionId) {
    context.regionId = regionId;
  }
  if (hubId) {
    context.hubId = hubId;
  }
  if (scopeTier) {
    context.scopeTier = scopeTier;
  }
  return context;
}

export function getCurrentUserId(): string | null {
  const user = getAuthUserFromCookie();
  const id = user?.id || user?.userId || user?.userid;
  return id ? String(id) : null;
}

export function getAuthToken(): string {
  return getAuthUserFromCookie()?.token || '';
}

export function mapError(error: unknown): ChatUiError {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 401 || error.status === 403) {
      return {
        kind: 'business_rule',
        message: 'The chat agent rejected the request. Check your access permissions.',
        retryable: false,
      };
    }
    if (error.status === 404) {
      return {
        kind: 'server',
        message: 'Chat endpoint not found (404). Verify backend is running with /api/ChatConversation/SendMessage or /api/chat/ask.',
        retryable: false,
      };
    }
    if (error.status === 405) {
      return {
        kind: 'server',
        message: 'Chat endpoint rejected HTTP method (405). Verify API route mapping.',
        retryable: false,
      };
    }
    if (error.status === 429) {
      return {
        kind: 'rate_limit',
        message: 'Too many chat requests right now. Please wait a moment and try again.',
        retryable: true,
      };
    }
    if (error.status === 0) {
      return {
        kind: 'unavailable',
        message: 'Unable to reach the chat agent (network/CORS/host down). Verify API host is running.',
        retryable: true,
      };
    }
    if (error.error?.error?.message) {
      return {
        kind: 'server',
        message: error.error.error.message,
        retryable: false,
      };
    }
    if (typeof error.error?.message === 'string') {
      return {
        kind: 'server',
        message: error.error.message,
        retryable: false,
      };
    }
    return {
      kind: 'server',
      message: 'Unable to reach the chat agent right now. Please try again.',
      retryable: true,
    };
  }
  if (error instanceof Error) {
    return {
      kind: 'unknown',
      message: error.message,
      retryable: false,
    };
  }
  return {
    kind: 'unknown',
    message: 'Something went wrong while talking to the chat agent. Please try again.',
    retryable: false,
  };
}

export function toChatUiError(response: ChatResponse): ChatUiError {
  const code = `${response.data?.error?.code || ''}`.trim().toLowerCase();
  const retryable = response.data?.error?.retryable === true;
  const message = `${response.message || response.data?.message || ''}`.trim() || 'Chat request failed.';

  switch (code) {
    case 'balance':
      return { kind: 'balance', message, retryable };
    case 'timeout':
      return { kind: 'timeout', message, retryable };
    case 'upstream_unavailable':
      return { kind: 'unavailable', message, retryable };
    case 'rate_limit':
      return { kind: 'rate_limit', message, retryable };
    case 'conversation_state':
      return { kind: 'conversation', message, retryable };
    case 'business_rule':
      return { kind: 'business_rule', message, retryable };
    case 'provider_error':
    case 'server_error':
      return { kind: 'server', message, retryable };
    default:
      return { kind: 'unknown', message, retryable };
  }
}

export function shouldRetryOnFallback(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  return error.status === 0 || error.status === 404 || error.status === 405;
}

export function isStatusMessage(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return normalized === 'ok'
    || normalized === 'done'
    || normalized === 'success'
    || normalized === 'created'
    || normalized === 'updated'
    || normalized === 'deleted';
}

function readRouteQueryParams(): URLSearchParams {
  try {
    const hash = typeof window !== 'undefined' ? window.location.hash || '' : '';
    const queryIndex = hash.indexOf('?');
    if (queryIndex >= 0) {
      return new URLSearchParams(hash.substring(queryIndex + 1));
    }

    const search = typeof window !== 'undefined' ? window.location.search || '' : '';
    return new URLSearchParams(search.startsWith('?') ? search.substring(1) : search);
  } catch {
    return new URLSearchParams();
  }
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return undefined;
  }

  return parsed;
}

function parsePositiveIntFromUnknown(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) {
    return undefined;
  }
  return parsed;
}

function readLoggedUser(): any | null {
  return getAuthUserFromCookie();
}

function resolveUserScopeTier(user: any): string {
  if (!user) {
    return '';
  }

  if (parsePositiveIntFromUnknown(user?.laboratoryId ?? user?.labId ?? user?.laboratory?.id)) {
    return 'facility';
  }
  if (parsePositiveIntFromUnknown(user?.woredaId ?? user?.woreda?.id)) {
    return 'woreda';
  }
  if (parsePositiveIntFromUnknown(user?.zoneId ?? user?.zone?.id)) {
    return 'zone';
  }
  if (parsePositiveIntFromUnknown(user?.regionId ?? user?.region?.id)) {
    return 'regional';
  }
  if (parsePositiveIntFromUnknown(user?.hubId ?? user?.hub?.id)) {
    return 'hub';
  }

  const rawRoles = user?.userRoles ?? user?.roles ?? user?.role ?? [];
  const roleList = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
  const roles = roleList
    .map((role: any) => {
      if (!role) {
        return '';
      }
      if (typeof role === 'string') {
        return role;
      }
      return String(role.name || role.roleName || role.value || '');
    })
    .map((role: string) => role.toLowerCase());

  if (roles.some((role: string) => role.includes('woreda'))) {
    return 'woreda';
  }
  if (roles.some((role: string) => role.includes('zone') || role.includes('zonal'))) {
    return 'zone';
  }
  if (roles.some((role: string) => role.includes('region') || role.includes('regional'))) {
    return 'regional';
  }
  if (roles.some((role: string) => role.includes('hub'))) {
    return 'hub';
  }

  return 'national';
}
